import { Calendar } from 'lucide-react'
import type { DateRange } from '../types'

interface TelemetryDateFilterProps {
  dateFrom: string
  dateTo: string
  dateRange: DateRange
  onChange: (params: { date_from?: string; date_to?: string; date_range?: string }) => void
}

function localDateStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function todayStr() {
  return localDateStr(new Date())
}

function daysAgo(days: number) {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return localDateStr(d)
}

function startOfMonth() {
  const d = new Date()
  return localDateStr(new Date(d.getFullYear(), d.getMonth(), 1))
}

function previousMonth(): { from: string; to: string } {
  const d = new Date()
  const first = new Date(d.getFullYear(), d.getMonth() - 1, 1)
  const last = new Date(d.getFullYear(), d.getMonth(), 0)
  return { from: localDateStr(first), to: localDateStr(last) }
}

interface Preset {
  label: string
  from: string
  to: string
}

function getPresets(): Preset[] {
  const today = todayStr()
  const prev = previousMonth()
  return [
    { label: 'Últimos 7 dias', from: daysAgo(6), to: today },
    { label: '30 dias', from: daysAgo(29), to: today },
    { label: 'Este mês', from: startOfMonth(), to: today },
    { label: 'Mês anterior', from: prev.from, to: prev.to },
  ]
}

function isPresetActive(preset: Preset, dateFrom: string, dateTo: string) {
  return dateFrom === preset.from && dateTo === preset.to
}

export default function TelemetryDateFilter({ dateFrom, dateTo, dateRange, onChange }: TelemetryDateFilterProps) {
  const presets = getPresets()
  const isAllTime = dateRange === 'all'

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Calendar className="h-4 w-4 text-muted-foreground hidden sm:block" />
      <input
        type="date"
        value={dateFrom}
        max={dateTo || todayStr()}
        onChange={(e) => onChange({ date_from: e.target.value || undefined, date_to: dateTo || undefined, date_range: undefined })}
        className="text-sm border border-border rounded-lg px-2 py-1.5 text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      />
      <span className="text-xs text-muted-foreground">até</span>
      <input
        type="date"
        value={dateTo}
        min={dateFrom || undefined}
        max={todayStr()}
        onChange={(e) => onChange({ date_from: dateFrom || undefined, date_to: e.target.value || undefined, date_range: undefined })}
        className="text-sm border border-border rounded-lg px-2 py-1.5 text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      />
      <div className="flex flex-wrap items-center gap-1">
        {presets.map((p, index) => (
          <button
            type="button"
            key={p.label}
            onClick={() => onChange({ date_from: p.from, date_to: p.to, date_range: undefined })}
            className={`px-2 py-1 text-xs rounded-md border transition-colors ${
              !isAllTime && (isPresetActive(p, dateFrom, dateTo) || (dateRange === 'default' && index === 0))
                ? 'border-violet-300 bg-violet-50 text-violet-700 font-medium'
                : 'border-border text-muted-foreground hover:bg-accent'
            }`}
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange({ date_from: undefined, date_to: undefined, date_range: 'all' })}
          className={`px-2 py-1 text-xs rounded-md border transition-colors ${
            isAllTime
              ? 'border-violet-300 bg-violet-50 text-violet-700 font-medium'
              : 'border-border text-muted-foreground hover:bg-accent'
          }`}
          title="Pode demorar mais em bases grandes"
        >
          Todo o período
        </button>
      </div>
    </div>
  )
}
